import React, { useEffect } from "react";
import { X, Search, Folder, File, FolderOpen } from "lucide-react";
import FileIcon, { SimpleFileIcon } from "../../elements/FileIcon";

const SearchModal = ({
  open,
  onClose,
  query,
  onQueryChange,
  onSearch,
  results,
  onSelectFile,
  onSelectFolder,
}) => {
  useEffect(() => {
    if (open && query) {
      onSearch();
    }
  }, [open, query, onSearch]);

  if (!open) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSearch();
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <Search className="w-5 h-5" />
            파일 및 폴더 검색
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 border-b border-gray-200">
          <form onSubmit={handleSubmit}>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={query}
                onChange={(e) => onQueryChange(e.target.value)}
                onKeyUp={(e) => e.key === "Enter" && onSearch()}
                placeholder="파일명 또는 폴더명으로 검색"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            </div>
          </form>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {!results ? (
            <div className="text-center py-12">
              <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">검색어를 입력하세요</p>
            </div>
          ) : results.files.length === 0 && results.folders.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">검색 결과가 없습니다</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* 폴더 결과 */}
              {results.folders.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-3">
                    폴더 ({results.total_folders || results.folders.length}개)
                  </h3>
                  <div className="space-y-2">
                    {results.folders.map((folder) => (
                      <button
                        key={folder.id}
                        onClick={() => {
                          onSelectFolder(folder);
                          onClose();
                        }}
                        className="w-full flex items-center gap-3 p-3 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors text-left"
                      >
                        <FolderOpen className="w-5 h-5 text-blue-500 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {folder.folder_name}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {folder.path || "/"}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* 파일 결과 */}
              {results.files.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-3">
                    파일 ({results.total_files || results.files.length}개)
                  </h3>
                  <div className="space-y-2">
                    {results.files.map((file) => (
                      <button
                        key={file.id}
                        onClick={() => {
                          onSelectFile(file);
                          onClose();
                        }}
                        className="w-full flex items-center gap-3 p-3 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors text-left"
                      >
                        <SimpleFileIcon
                          type={file.file_type}
                          size={24}
                          className="flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {file.file_name}
                          </p>
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span>{formatFileSize(file.file_size)}</span>
                            <span>•</span>
                            <span className="truncate">
                              {file.folder_path || "/"}
                            </span>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchModal;
